import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import currency from "currency.js";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import QuantitySelector from "../QuantitySelector";
import { useTranslation } from "react-i18next";
import { useUpdateItemQuantity } from "@apis/cartRequest";
import { useEffect, useRef, useState } from "react";
import { ProductCategory } from "@utils/constants";
import { CartItem } from "@src/types/CartItemListResponse";
import { useUnifiedProductPagesSlice } from "@redux/slices/unifiedProductPages";
import { getShowEditIcon } from "@scenes/bulk-request/Utils";
import { isMeleeItem } from "@utils/product/productValidators";
import React from "react";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { useOrderCart } from "@redux/slices/globalSlice";

const PriceContainer = ({
  mItem,
  userInfo,
  setShowLoader,
  refetchCartData,
  orderCartRequest = false,
  isChildRequest = false,
  itemPrice,
  isBuilderItem,
  isOrderCart = false,
}: {
  mItem?: CartItem;
  userInfo?: any;
  setShowLoader?: any;
  refetchCartData?: any;
  orderCartRequest?: boolean;
  isChildRequest?: boolean;
  itemPrice?: string;
  isBuilderItem?: boolean;
  isOrderCart?: boolean;
}) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(mItem?.quantity);
  const userCurrency = useUserCurrency();
  const unifiedProductPagesSlice = useUnifiedProductPagesSlice();
  const globalOrderCart = useOrderCart();
  const quantity_selector = isOrderCart
    ? globalOrderCart?.quantity_selector
    : unifiedProductPagesSlice?.unifiedProductData?.[mItem?.stock_type]
        ?.orderCart?.quantity_selector;
  const showQuantitySelector =
    getShowEditIcon(quantity_selector, userInfo.getPriceMode()) ?? false;
  const {
    mutate: updateQuantity,
    isLoading,
    isSuccess,
  } = useUpdateItemQuantity();
  const didRefetchOnSuccess = useRef(false);

  useEffect(() => {
    if (isLoading) {
      didRefetchOnSuccess.current = false;
      setShowLoader(true);
      return;
    }
    if (isSuccess && !didRefetchOnSuccess.current) {
      didRefetchOnSuccess.current = true;
      refetchCartData();
    }
  }, [isLoading, isSuccess, refetchCartData, setShowLoader]);

  const handleQuantityChange = (newQty: number) => {
    setQuantity(newQty);
    updateQuantity({
      cartType: "order_cart",
      priceMode: userInfo.user.price_mode,
      id: mItem?.id?.toString(),
      quantity: newQty,
    });
  };

  return (
    <ParentContainer>
      <OverLay>
        <TextContainer>
          <VDBText color={theme["core-06"]} type={typography["Body-R"]}>
            {t("orderCart.itemCard.totalPrice.text")}
          </VDBText>
        </TextContainer>
        {showQuantitySelector &&
          !orderCartRequest &&
          !isBuilderItem &&
          mItem?.stock_type === ProductCategory.JEWELRY && (
            <QuantitySelector
              max={
                mItem?.stock_type === ProductCategory.JEWELRY
                  ? mItem?.item?.allow_backorder === undefined
                    ? 99
                    : mItem?.item?.allow_backorder
                    ? mItem?.max_quantity_allowed
                    : Number(mItem?.item.available_stock)
                  : 99
              }
              defaultValue={quantity}
              onChange={handleQuantityChange}
            />
          )}
        {orderCartRequest &&
          !isBuilderItem &&
          mItem?.stock_type === ProductCategory.JEWELRY && (
            <>
              <Spacer size={SpaceType.Basic} />
              <VDBText color={theme["core-06"]} type={typography["Body-R"]}>
                {t("orderCart.itemCard.quantityLabel.text")}
              </VDBText>
              <Spacer size={SpaceType.ExtraSmall} />
              <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
                {mItem?.quantity}
              </VDBText>
            </>
          )}
      </OverLay>
      <PriceView>
        <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
          {currency(
            itemPrice ||
              (orderCartRequest
                ? isChildRequest
                  ? mItem?.total_sales_price
                  : mItem?.total_price_based_on_quantity
                : mItem?.total_price),
            {
              symbol:
                (orderCartRequest || isBuilderItem
                  ? mItem?.currency_symbol
                  : mItem?.item?.currency_symbol) || userCurrency,
              precision:
                (userInfo?.roundUpPrices() &&
                  !isMeleeItem(
                    orderCartRequest || isBuilderItem ? mItem : mItem?.item
                  )) ||
                (orderCartRequest || isBuilderItem
                  ? mItem?.stock_type
                  : mItem?.item?.type) === ProductCategory.JEWELRY
                  ? 0
                  : 2,
            }
          ).format()}
        </VDBText>
      </PriceView>
    </ParentContainer>
  );
};

const ParentContainer = styled.View`
  padding: ${SpaceType.Basic}px;
  background-color: ${({ theme }) => theme["core-01"]};
  justify-content: space-between;
  flex-direction: row;
  border-bottom-left-radius: ${SpaceType.Basic}px;
  border-bottom-right-radius: ${SpaceType.Basic}px;
`;

const TextContainer = styled.View`
  width: 155px;
  justify-content: center;
`;
const OverLay = styled.View`
  flex-direction: row;
`;

const PriceView = styled.View`
  justify-content: center;
`;

export default PriceContainer;
